import type { AppState, ScientificFrame } from '../science/types';
import type { EraVisualPackage } from '../eras/types';
import type { SceneCallbacks, SceneController } from './SceneController';
import { CanvasFieldFallback } from './CanvasFieldFallback';

const supportsWebGL = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
};

class DeferredThreeScene implements SceneController {
  private delegate: SceneController | null = null;
  private state: Readonly<AppState> | null = null;
  private frame: ScientificFrame | null = null;
  private profile: EraVisualPackage | null = null;
  private tool = 'orbit';
  private disposed = false;

  constructor(private host: HTMLElement, private callbacks: SceneCallbacks) {
    this.host.dataset.renderer = 'loading-three';
    void import('./FieldScene').then(({ FieldScene }) => {
      if (this.disposed) return;
      this.delegate = new FieldScene(this.host, this.callbacks);
      this.host.dataset.renderer = 'three';
      if (this.profile) this.delegate.setVisualProfile?.(this.profile);
      if (this.state && this.frame) this.delegate.update(this.state, this.frame);
      this.delegate.setTool(this.tool);
    }).catch((error) => {
      if (this.disposed) return;
      console.warn('Three.js renderer unavailable; using the 2D compatibility renderer.', error);
      this.host.replaceChildren();
      this.delegate = new CanvasFieldFallback(this.host);
      this.host.dataset.renderer = 'canvas-fallback';
      if (this.profile) this.delegate.setVisualProfile?.(this.profile);
      if (this.state && this.frame) this.delegate.update(this.state, this.frame);
    });
  }

  update(state: Readonly<AppState>, frame: ScientificFrame): void {
    this.state = state;
    this.frame = frame;
    this.delegate?.update(state, frame);
  }

  setVisualProfile(profile: EraVisualPackage): void {
    this.profile = profile;
    this.delegate?.setVisualProfile?.(profile);
  }

  setTool(tool: string): void {
    this.tool = tool;
    this.delegate?.setTool(tool);
  }

  step(deltaSeconds = 1 / 30): void {
    this.delegate?.step(deltaSeconds);
  }

  snapshot(): string {
    if (this.delegate) return this.delegate.snapshot();
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, this.host.clientWidth || 1200);
    canvas.height = Math.max(1, this.host.clientHeight || 600);
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = '#041426';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#9ec9e9';
      context.font = '20px sans-serif';
      context.fillText('Three.js renderer is loading.', 32, 48);
    }
    return canvas.toDataURL('image/png');
  }

  dispose(): void {
    this.disposed = true;
    this.delegate?.dispose();
    this.delegate = null;
  }
}

export const createScene = (host: HTMLElement, callbacks: SceneCallbacks = {}): SceneController => {
  const forcedFallback = new URLSearchParams(location.search).get('no3d') === '1';
  if (forcedFallback || !supportsWebGL()) {
    host.dataset.renderer = 'canvas-fallback';
    return new CanvasFieldFallback(host);
  }
  return new DeferredThreeScene(host, callbacks);
};
